/**
 * Layout and routing constants for the analysis workspace canvas.
 *
 * Tweak the values here to change layout density or per-row caps without
 * touching the placement engine or the slice.
 */

/** Composite-id prefix used by the Pantheon `WidgetSource` (see `pantheon-source.tsx`). */
export const PANTHEON_SOURCE_NAME = 'pantheon';

/**
 * Canvas `lg` breakpoint threshold (px), mirroring `DEFAULT_BREAKPOINTS.lg` from
 * `@amzn/ad-console-canvas` (not exported by its public API). Below this width the
 * canvas switches to the stacked md/sm layout, where the expand/collapse toggle is hidden.
 */
export const LG_BREAKPOINT_PX = 768;

/** Number of grid columns. KPIs use 3 cols (4 per row); charts use 6 (2 per row). */
export const GRID_COLS = 12;
/**
 * Row height (px). Sized so KPIs land ~100px tall at h=2 while keeping charts and
 * the summary card on a small number of rows — fewer rows means less cumulative
 * inter-row margin compounding inside each cell, which keeps cards visually tight.
 */
export const ROW_HEIGHT = 42;

export const MAX_WIDGETS_TOTAL = 36;
export const MAX_CHARTS_PER_ROW = 2;
export const MAX_KPIS_PER_ROW = 3;

/** Per-category widget dimensions in grid units (h is multiplied by `ROW_HEIGHT` at render time). */
export const WIDGET_DIMENSIONS = {
  SUMMARY: { w: 12, h: 1, flexHeight: true },
  CHART: { w: 6, h: 8, flexHeight: false },
  TABLE: { w: 12, h: 1, flexHeight: true },
  KPI: { w: 4, h: 2, flexHeight: false }
} as const;

export enum WidgetCategory {
  SUMMARY = 'SUMMARY',
  CHART = 'CHART',
  TABLE = 'TABLE',
  KPI = 'KPI'
}

/** Returns the grid dimensions for a widget category. */
export const getWidgetDimensions = (category: WidgetCategory): { w: number; h: number; flexHeight: boolean } => {
  switch (category) {
    case WidgetCategory.SUMMARY:
      return WIDGET_DIMENSIONS.SUMMARY;
    case WidgetCategory.CHART:
      return WIDGET_DIMENSIONS.CHART;
    case WidgetCategory.TABLE:
      return WIDGET_DIMENSIONS.TABLE;
    case WidgetCategory.KPI:
      return WIDGET_DIMENSIONS.KPI;
  }
};

/**
 * Maps a `visualizationSpec.type` (or the literal `'markdown'` for `contentType: 'markdown'`)
 * to the layout category that drives sizing and per-row caps.
 *
 * Only types listed here are routable on the canvas — anything else is rejected
 * by the slice. New chart types come online by adding an entry here AND in
 * `VIZ_TYPE_TO_PANTHEON_MODULE_ID`.
 */
export const VIZ_TYPE_TO_CATEGORY: Record<string, WidgetCategory> = {
  markdown: WidgetCategory.SUMMARY,
  metric: WidgetCategory.KPI,
  bar: WidgetCategory.CHART,
  line: WidgetCategory.CHART,
  area: WidgetCategory.CHART,
  combo: WidgetCategory.CHART,
  pie: WidgetCategory.CHART,
  bubble: WidgetCategory.CHART,
  column: WidgetCategory.CHART,
  heatmap: WidgetCategory.CHART,
  scatter: WidgetCategory.CHART,
  pyramid: WidgetCategory.CHART,
  table: WidgetCategory.CHART,
  waterfall: WidgetCategory.CHART
};

/**
 * Maps a `visualizationSpec.type` to the Pantheon-federated module that renders it.
 * The module id is combined with `PANTHEON_SOURCE_NAME` to form
 * `BoardWidget.type` as `"pantheon:<moduleId>"`.
 */
export const VIZ_TYPE_TO_PANTHEON_MODULE_ID: Record<string, string> = {
  markdown: 'canvas-widget-summary-card',
  metric: 'canvas-widget-kpi',
  bar: 'canvas-widget-bar-chart',
  line: 'canvas-widget-line-chart',
  area: 'canvas-widget-cartesian-chart',
  combo: 'canvas-widget-cartesian-chart',
  pie: 'canvas-widget-pie-chart',
  bubble: 'canvas-widget-bubble-chart',
  column: 'canvas-widget-column-chart',
  heatmap: 'canvas-widget-heat-map',
  scatter: 'canvas-widget-scatter-chart',
  pyramid: 'canvas-widget-pyramid-chart',
  table: 'canvas-widget-table',
  waterfall: 'canvas-widget-waterfall-chart'
};
